<template>
    <div>
        <div class="d-flex swimlane-container flex-nowrap">
            <div v-for="(lane, index) in swimlanes" :key="lane.status.value" class="swimlane-wrapper col-sm-6 col-md-4 flex">
                <div :class="['swimlane-' + lane.status.value]" class="swimlane">
                    <header class="swimlane-header">
                        {{ lane.status.label }}
                        <span>({{ getWorkOrderCountForLane(lane.status.value) }})</span>
                    </header>
                    <div class="swimlane-body">
                        <div v-if="showEmptyState && index === 0" class="flex pt-4 pb-4 text-center">
                            <b>{{ trans('workorder.overview.board.empty') }}</b>
                        </div>
                        <div v-else>
                            <work-order-card
                                v-for="workOrder in getWorkOrdersForStatus(lane.status.value)"
                                :key="`${workOrder.id}-${workOrder.updatedAt}`"
                                :ref="(el) => registerCardRef(el, workOrder.id)"
                                :active="workOrdersStore.highlightedWorkOrderId === workOrder.id"
                                :is-loading="workOrdersStore.isProcessing(workOrder.id)"
                                :work-order="workOrder"
                                @click="loadDetails(workOrder)"
                                @delete="requestWorkOrderDeletionConfirmation"
                                @move-to="moveToStatus"
                            />
                        </div>
                        <div :ref="(el) => registerLandmark(el, lane.status.value)" :data-status="lane.status.value" class="flex justify-center p-2">
                            <spinner v-if="isLoading(lane.status.value)" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <confirm-work-order-deletion-modal />
        <resolve-preventative-work-order-modal ref="resolvePreventativeModalRef" name="resolve-preventative-board" />
    </div>
</template>

<script setup>
import ConfirmWorkOrderDeletionModal from '@/Pages/WorkOrders/Modals/ConfirmWorkOrderDeletionModal.vue';
import ResolvePreventativeWorkOrderModal from '@/Pages/WorkOrders/Modals/ResolvePreventativeWorkOrderModal.vue';
import WorkOrderCard from '@/Pages/WorkOrders/WorkOrderCard.vue';
import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { loadWorkOrderDetails } from '@/Pages/WorkOrders/loadWorkOrderDetails';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useWorkOrderStore } from '@/stores/workOrders';
import { trans } from '@/mixins/i18n.js';
import { hasFeature } from '@/mixins/hasFeature.js';

const props = defineProps({
    selectedWorkOrder: { type: Object, default: null },
    swimlanes: { type: Array, required: true },
});

const workOrdersStore = useWorkOrderStore();

const resolvePreventativeModalRef = ref(null);
const observer = ref(null);
const landmarks = ref({});
const cardRefs = ref({});

const totalWorkOrderCount = computed(() => {
    return ['to_do', 'in_progress', 'done'].reduce((sum, status) => {
        return sum + (workOrdersStore.lanes?.[status]?.meta?.total || 0);
    }, 0);
});

const showEmptyState = computed(() => {
    return !isAnyLaneLoading.value && !totalWorkOrderCount.value;
});

const isAnyLaneLoading = computed(() => {
    return ['to_do', 'in_progress', 'done'].some((status) => workOrdersStore.lanes?.[status]?.isLoading);
});

const getWorkOrdersForStatus = (status) => {
    return workOrdersStore.lanes?.[status]?.items || [];
};

const isLoading = (status) => {
    return workOrdersStore.lanes?.[status]?.isLoading || false;
};

const getWorkOrderCountForLane = (status) => {
    return workOrdersStore.lanes?.[status]?.meta?.total || 0;
};

const registerLandmark = (el, status) => {
    if (!el || !status) return;
    landmarks.value[status] = el;
};

const registerCardRef = (el, workOrderId) => {
    if (!el || !workOrderId) return;
    cardRefs.value[workOrderId] = el;
};

const setupSwimlaneObserver = () => {
    observer.value = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const status = entry.target?.dataset?.status;
                if (!status) return;
                workOrdersStore.loadMore(status);
            });
        },
        { root: null, threshold: 0.3 },
    );
};

const observeSwimlanes = () => {
    Object.values(landmarks.value).forEach((el) => {
        if (!el) return;
        observer.value?.observe(el);
    });
};

const requestWorkOrderDeletionConfirmation = async (workOrder) => {
    workOrdersStore.setWorkOrderLoading(workOrder.id, true);

    try {
        const { data } = await $http.get(route('workorders.show', workOrder));
        const fresh = data.workOrder;
        const canBeDeleted = fresh.status.value !== 'done' && fresh.isEditable;

        if (!canBeDeleted) {
            eventBus.$emit(events.presentFlashMessage, {
                payload: {
                    message: trans('workorders.message.outdated_refreshed'),
                    status: 'error',
                },
            });
            workOrdersStore.setWorkOrderLoading(workOrder.id, false);
            workOrdersStore.reloadLanes();
            return;
        }
    } catch {
        eventBus.$emit(events.presentFlashMessage, {
            payload: {
                message: trans('general.error.unknown'),
                status: 'error',
            },
        });
        workOrdersStore.setWorkOrderLoading(workOrder.id, false);
        workOrdersStore.reloadLanes();
        return;
    }

    workOrdersStore.setWorkOrderLoading(workOrder.id, false);

    eventBus.$emit(events.openDeleteWorkOrderModal, {
        workOrder,
        onDeleting: (wo) => workOrdersStore.setWorkOrderLoading(wo?.id, true),
        onDeleted: (wo) => {
            eventBus.$emit(events.workOrderDeleted, wo.id);
        },
        onError: (wo) => {
            workOrdersStore.setWorkOrderLoading(wo?.id, false);
            workOrdersStore.reloadLanes();
        },
    });
};

const loadDetails = (workOrder) => {
    if (!workOrder) return;

    workOrdersStore.highlightedWorkOrderId = workOrder.id;

    const url = new URL(window.location.href);
    url.searchParams.set('workorder', workOrder.id);
    window.history.pushState({}, '', url.toString());

    workOrdersStore.setWorkOrderLoading(workOrder.id, true);
    loadWorkOrderDetails(workOrder.id).finally(() => {
        workOrdersStore.setWorkOrderLoading(workOrder.id, false);
    });
};

const moveToStatus = ({ workOrder, status }) => {
    if (!workOrder?.id || !status) return;

    const requiresResolvedDate =
        status === 'done' && workOrder.type?.value === 'preventative_maintenance' && hasFeature('schedule_preventative_maintenance_work_orders');

    if (requiresResolvedDate) {
        resolvePreventativeModalRef.value?.open({
            workOrder,
            onConfirm: (resolvedDate) => workOrdersStore.updateStatus(workOrder, status, resolvedDate),
        });
        return;
    }

    workOrdersStore.updateStatus(workOrder, status);
};

watch(
    () => workOrdersStore.highlightedWorkOrderId,
    (id) => {
        if (!id) return;
        nextTick(() => {
            const component = cardRefs.value?.[id];
            const el = component?.$el ?? component;
            el?.scrollIntoViewIfNeeded && el.scrollIntoViewIfNeeded();
        });
    },
    { immediate: true },
);

onMounted(() => {
    setupSwimlaneObserver();

    nextTick(() => {
        observeSwimlanes();

        if (props.selectedWorkOrder) {
            loadDetails(props.selectedWorkOrder);
        }
    });
});

onBeforeUnmount(() => {
    observer.value?.disconnect?.();
});
</script>
